import { useCallback } from 'react';
import { useAiPanelState, type AiPanelContextKind } from './AiPanelContext';
import { useTripContext } from '../../pages/trip/TripDetailPage';
import { applyItineraryAiActions } from '../../pages/trip/itineraryAiApply';
import type { AiPersistedAction } from '../../api/aiProposals';

// Which tab-domain owns each persisted action type. Itinerary actions are
// applied directly against the page-level `days` state; the rest go to the
// owning tab's registered handler (local state) if it's mounted.
function domainForAction(type: AiPersistedAction['type']): AiPanelContextKind {
  switch (type) {
    case 'venue_created':
    case 'venues_deleted':
      return 'places';
    case 'tips_created':
      return 'tips';
    case 'packing_sections_created':
    case 'packing_items_updated':
      return 'packing';
    case 'booking_created':
      return 'bookings';
    // Notes and meals tabs refetch on mount and register no handler.
    case 'note_created':
    case 'meals_updated':
    case 'entity_changed':
    case 'operation_applied':
      return 'general';
    case 'expense_created':
    case 'expenses_deleted':
    case 'budget_set':
      return 'expenses';
    default:
      return 'itinerary';
  }
}

const ITINERARY_TYPES = new Set<AiPersistedAction['type']>([
  'activity_created', 'activity_moved', 'activity_deleted', 'day_plan_applied', 'activities_reordered',
  'activity_updated',
]);

/**
 * Central dispatcher for AI proposals accepted in the docked panel. Routes each
 * persisted action to the right place regardless of which tab is active — this
 * is what makes the section-independent assistant actually apply its changes:
 *
 * - Itinerary actions → page-level `setDays` (TripContext), always mounted, so
 *   they apply from any tab and ItineraryTab reflects them via context.
 * - Venue/tips/packing actions → the owning tab's handler if mounted; otherwise
 *   the entity is already persisted server-side and appears when that tab is
 *   next visited (it refetches on mount).
 */
export function useAiActionDispatch() {
  const { setDays, loadDays, reload } = useTripContext();
  const { handlersRef, bumpData } = useAiPanelState();

  return useCallback((actions: AiPersistedAction[]) => {
    // The proposals are already persisted server-side by the time we get here —
    // this is only the optimistic local UI update. A failure to reflect it in a
    // tab's state must never bubble up and be mistaken for a save failure, so
    // each apply is isolated.
    const itinerary = actions.filter(a => ITINERARY_TYPES.has(a.type));
    if (itinerary.length > 0) {
      try { applyItineraryAiActions(setDays, itinerary); }
      catch (e) { console.error('[ai] itinerary UI update failed', e); }
    }

    for (const action of actions) {
      if (action.type === 'operation_applied') {
        bumpData(action.area);
        if (action.area === 'itinerary' || action.area === 'meals') void loadDays();
        // New dates change the trip itself (header, day count), not only its days.
        if (action.op === 'trip_dates') reload();
        continue;
      }
      if (action.type === 'entity_changed') {
        // ADR-0028: the server applied it; the owning tab re-reads. Bookings and
        // meals also live in the itinerary as cards, so the days follow.
        bumpData(action.area);
        if (action.area === 'itinerary' || action.area === 'bookings' || action.area === 'meals') void loadDays();
        continue;
      }
      if (ITINERARY_TYPES.has(action.type)) continue;
      const handler = handlersRef.current.get(domainForAction(action.type));
      try { handler?.([action]); }
      catch (e) { console.error('[ai] action UI update failed', e); }
    }
  }, [setDays, loadDays, reload, handlersRef, bumpData]);
}
